import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Filter from 'components/filter';
import ExportModal from 'components/modals/export';
import Pagination from 'components/pagination';
import Search from 'components/search';
import ExportBtn from 'components/modals/export/exportBtn';
import Table from 'components/table';
import ToastContainer, { ToastType } from 'components/toasts/container';
import PaymentStatus from 'views/settings/config/tabs/components/modals/paymentStatus';
import PaymentAdjustment from 'components/modals/paymentAdjustment';
import { updateStatusOnPaymentTypeChange } from 'utils/filter';
import { EFilterConfigType, IFilterConfig } from 'components/filter/types';
import {
  GTW_STATUS,
  paymentTypeList,
  statusList,
  SELECTED_ENTITY_IDS_PARAM,
  FILTER_PARAM_KEYS,
} from 'utils/constants';
import { PAYMENTS } from 'state/payment/type';
import usePayment from 'state/payment/hooks/usePayment';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import useOrgLevel from 'utils/hooks/useOrgLevel';
import useStore from 'state/store/hooks/useStore';
import useQuery from 'utils/hooks/useQuery';
import { useStoreName } from 'utils/hooks/useStoreName';
import { useTransactionDetails } from './hooks/useTransactionDetails';
import { useTransactionActions } from './hooks/useTransactionActions';
import { createTransactionColumns } from './components/TransactionColumns';
import { PERMISSION_KEYS, TOAST_MESSAGES } from './constants';
import { TransactionDetailsTabProps, TransactionTableData } from './types';

// Re-export types for backwards compatibility
export type { ITableData, TransactionTableData } from './types';

const TransactionDetailsTab = ({
  dateFrom,
  dateTo,
  triggerRefresh,
  isActiveTab,
}: TransactionDetailsTabProps) => {
  const navigate = useNavigate();
  const urlParams = useQuery(location?.search);
  const isOrgLevel = useOrgLevel();
  const { getStoreName } = useStoreName();
  const { store } = useStore();

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showPaymentStatusModal, setShowPaymentStatusModal] = useState(false);
  const [recordSale, setRecordSale] = useState(false);
  const [recordSaleData, setRecordSaleData] = useState<TransactionTableData>();
  const [refreshTnx, setRefreshTnx] = useState(false);
  const [adjustmentToast, setAdjustmentToast] = useState(false);

  // Hooks for data and actions
  const {
    tableData,
    isLoading,
    isEmpty,
    prevKey,
    nextKey,
    numResults,
    prevPage,
    nextPage,
    fetchTransactions,
  } = useTransactionDetails({
    dateFrom,
    dateTo,
    isActiveTab,
    urlParams,
    triggerRefresh,
  });

  const { toastType, warningType, toastMessage, toastSubDetails, handleRefund, clearToast } =
    useTransactionActions();

  // Payment hooks
  const {
    payment,
    toFetchCSVTransactions,
    toClearTransactionsExport,
    toResetPayment,
    isGatewayTransactionID,
    resetPaymentAdjustment,
  } = usePayment();

  const { paymentConfig, getPaymentStatus } = usePaymentConfig();
  const { paymentStatus } = paymentConfig;
  const { url, exportResponse, error } = payment;

  const { organisation } = useOrganisation();
  const { settings, activeOrg } = organisation;

  // Permission check for record sale action
  const entityPermissions = paymentConfig?.msoPermissionsList?.entityContexts?.find(
    (entity) => entity.id === activeOrg?.treez_entity_id
  );
  const canRecordSale =
    entityPermissions?.permissions?.includes(PERMISSION_KEYS.CREATE_PAYMENT_ADJUSTMENT) ?? false;

  // URL params for filters
  const amountOption = urlParams.get(FILTER_PARAM_KEYS.CONDITION) || 'equal';
  const firstAmount = urlParams.get(FILTER_PARAM_KEYS.AMOUNT_1) || undefined;
  const secondAmount = urlParams.get(FILTER_PARAM_KEYS.AMOUNT_2) || undefined;
  const statusArray = urlParams.get(FILTER_PARAM_KEYS.STATUS)?.split(',') || [];
  const paymentTypeArray = urlParams.get(FILTER_PARAM_KEYS.PAYMENT_TYPE)?.split(',') || [];
  const storeIdArray = urlParams.get(SELECTED_ENTITY_IDS_PARAM.KEY)?.split(',') || [];

  // Action handlers
  const handleVerifySale = (row: TransactionTableData) => {
    if (!row.gateway_id) return;
    getPaymentStatus(row.gateway_id).then(() => {
      if (isActiveTab) {
        setShowPaymentStatusModal(true);
      }
    });
  };

  const handleRecordSale = (row: TransactionTableData) => {
    setRecordSaleData(row);
    setRecordSale(true);
  };

  // Row click handler
  const handleRowClick = (row: PAYMENTS.ITransactionResult, _: SyntheticEvent) => {
    if (isGatewayTransactionID(row.id)) navigate(row?.gateway_id || '');
    else navigate(row?.order_id || '');
  };

  // Column configuration
  const columns = useMemo(
    () =>
      createTransactionColumns({
        settings,
        isOrgLevel,
        isActiveTab,
        getStoreName,
        isGatewayTransactionID,
        onVerifySale: handleVerifySale,
        onRecordSale: handleRecordSale,
        onRefund: handleRefund,
        canRecordSale,
      }),
    [settings, isOrgLevel, isActiveTab, getStoreName, isGatewayTransactionID, canRecordSale]
  );

  // Filter configuration
  const amountValues = ['equal'];
  if (amountOption) amountValues[0] = amountOption;
  if (firstAmount) amountValues[1] = firstAmount;
  if (secondAmount) amountValues[2] = secondAmount;

  const storeOptions = store.storesList
    ? store.storesList
        .filter((s) => s.has_access)
        .map((s) => ({
          name: s.dba_name,
          value: s.entity_id,
        }))
    : [];

  const filterConfig: IFilterConfig[] = useMemo(() => {
    const filters: IFilterConfig[] = [
      {
        key: FILTER_PARAM_KEYS.AMOUNT,
        label: 'Amount',
        type: EFilterConfigType.number,
        data: {
          keys: ['condition'],
          values: amountValues,
        },
        selected: false,
      },
      {
        key: FILTER_PARAM_KEYS.PAYMENT_TYPE,
        label: 'Payment Type',
        type: EFilterConfigType.list,
        data: {
          keys: FILTER_PARAM_KEYS.PAYMENT_TYPE,
          options: paymentTypeList,
          values: paymentTypeArray,
        },
        selected: paymentTypeArray?.length > 0,
      },
      {
        key: FILTER_PARAM_KEYS.STATUS,
        label: 'Transaction status',
        type: EFilterConfigType.list,
        data: {
          keys: FILTER_PARAM_KEYS.STATUS,
          options: statusList,
          values: statusArray,
        },
        selected: statusArray?.length > 0,
      },
    ];

    if (isOrgLevel && storeOptions.length > 0) {
      filters.push({
        key: SELECTED_ENTITY_IDS_PARAM.KEY,
        label: 'Store',
        type: EFilterConfigType.list,
        data: {
          keys: SELECTED_ENTITY_IDS_PARAM.KEY,
          options: storeOptions,
          values: storeIdArray,
        },
        selected: storeIdArray?.length > 0,
      });
    }

    return filters;
  }, [amountValues, paymentTypeArray, statusArray, isOrgLevel, storeOptions, storeIdArray]);

  const dateFilterConfig: IFilterConfig = {
    key: 'dates',
    label: 'Create Date',
    type: EFilterConfigType.date,
    data: {
      values: [dateFrom, dateTo],
    },
    selected: false,
  };

  const exportFilterConfig = [dateFilterConfig].concat(filterConfig);

  // Effects
  useEffect(() => {
    return () => {
      toResetPayment();
    };
  }, []);

  useEffect(() => {
    if (!showPaymentStatusModal && paymentStatus === GTW_STATUS.APPROVED) {
      fetchTransactions();
    }
  }, [showPaymentStatusModal]);

  useEffect(() => {
    if (refreshTnx) {
      fetchTransactions();
      setRefreshTnx(false);
    }
  }, [refreshTnx]);

  useEffect(() => {
    if (payment.capturePaymentAdjustment === true) {
      setAdjustmentToast(true);
      resetPaymentAdjustment();
      setShowModal(false);
    }
  }, [payment.capturePaymentAdjustment]);

  return (
    <>
      <div className="flex mb-3">
        <span className="mr-auto">
          <Search queryParams={location.search} />
        </span>

        {filterConfig && (
          <>
            <Filter
              config={updateStatusOnPaymentTypeChange(filterConfig)}
              queryParams={location.search}
              changeKey={FILTER_PARAM_KEYS.PAYMENT_TYPE}
              changeKeyUpdateHandlerCb={updateStatusOnPaymentTypeChange}
            />
            <ExportBtn onClick={() => setShowModal(true)} id="tnx-export-btn" />
          </>
        )}
      </div>

      <Table
        data={tableData}
        columns={columns}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        isEmpty={isEmpty}
        defaultSortKey="transactionDate"
      />

      <Pagination
        pageId={location?.search}
        results={numResults}
        prevPage={prevPage}
        nextPage={nextPage}
        prevKey={prevKey}
        nextKey={nextKey}
      />

      {showModal && (
        <ExportModal
          showModal={showModal}
          setShowModal={setShowModal}
          config={updateStatusOnPaymentTypeChange(exportFilterConfig)}
          heading="Transaction Summary"
          onExport={toFetchCSVTransactions}
          reportUrl={url}
          exportResponse={exportResponse}
          label="Transactions"
          errorMessage={error.addUrl?.message ?? undefined}
          toClearExport={toClearTransactionsExport}
          changeKey={FILTER_PARAM_KEYS.PAYMENT_TYPE}
          changeKeyUpdateHandlerCb={updateStatusOnPaymentTypeChange}
        />
      )}

      {showPaymentStatusModal && (
        <PaymentStatus
          showModal={showPaymentStatusModal}
          setShowModal={setShowPaymentStatusModal}
          status={paymentStatus === GTW_STATUS.APPROVED}
        />
      )}

      {recordSale && recordSaleData && (
        <PaymentAdjustment
          refreshTnx={refreshTnx}
          setRefreshTnx={setRefreshTnx}
          showModal={recordSale}
          setShowModal={(arg) => setRecordSale(arg)}
          info={recordSaleData}
        />
      )}

      <ToastContainer
        type={warningType}
        showToast={Boolean(toastType)}
        onClose={clearToast}
        message={toastMessage}
        id={toastType?.toLowerCase() || ''}
        data-testid="cash-toast-success"
        details={toastSubDetails}
      />

      <ToastContainer
        showToast={adjustmentToast}
        onClose={() => setAdjustmentToast(false)}
        message={TOAST_MESSAGES.SUCCESS_ADJUSTMENT}
        type={ToastType.success}
        showCross={true}
      />
    </>
  );
};

export default TransactionDetailsTab;
